import React from 'react';
import { Image } from '../../commonTypes/image';
import { CallToAction } from '../../commonTypes/links';
import colors from '../../helpers/colors';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { generateKeyForElement } from '../../helpers/elementKeyHelper';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { applyComponentTheme } from '../../theme';
import { BackgroundType } from '../Background';
import { Grid } from '../Grid';
import {
  aboveHeadingGridItemProps,
  displayHeadingInline,
  imageGridItemProps,
  sectionsGridItemProps,
} from './layout';
import {
  CtasContainer,
  CtasGridItem,
  FourSectionGridItem,
  Heading,
  HeadingGridItem,
  HeadingWrapper,
  ImageGridItem,
  InlineHeadingWrapper,
  MiniGuideContainer,
  Section,
  SectionGridItem,
  SectionsContainer,
  SectionText,
  StyledBackground,
  StyledGrid,
  StyledIcon,
  StyledImage,
  StyledPrimaryCta,
  StyledSecondaryCta,
  Subheading,
} from './styles';

export interface MiniGuideProps extends ComponentProps {
  styledBackground?: boolean;
  /** @deprecated use backgroundType of BackgroundType.CHEVRON instead */
  backgroundType?: BackgroundType;
  title: string;
  subtitle?: string;
  sections: string[];
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
  image?: Image;
  customBulletIcon?: string;
  forceLargeImageOnTablet?: boolean;
  forceUseAllColumns?: boolean;
}

type MiniGuideBodyProps = {
  shouldDisplayHeadingInline: boolean;
} & MiniGuideProps;

type CtasProps = {
  noOfSections: number;
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
};

const Ctas: React.FC<CtasProps> = ({ noOfSections, primaryCta, secondaryCta }) => (
  <CtasContainer noOfSections={noOfSections}>
    {primaryCta && <StyledPrimaryCta cta={primaryCta} />}
    {secondaryCta && <StyledSecondaryCta cta={secondaryCta} />}
  </CtasContainer>
);

const MiniGuideBody: React.FC<MiniGuideBodyProps> = ({
  shouldDisplayHeadingInline,
  title,
  subtitle,
  sections,
  primaryCta,
  secondaryCta,
  customBulletIcon,
  forceUseAllColumns,
  forceLargeImageOnTablet,
}) => {
  const shouldRenderCtas = !!primaryCta || !!secondaryCta;

  if (sections.length === 4) {
    return (
      <>
        {sections.map((section, index) => (
          <FourSectionGridItem
            key={generateKeyForElement([section, index])}
            {...sectionsGridItemProps(
              sections.length,
              forceUseAllColumns,
              forceLargeImageOnTablet
            )}>
            <Section tabIndex={0} noOfSections={sections.length} index={index}>
              {customBulletIcon && (
                <StyledIcon name={customBulletIcon} size="medium" color={colors.core01} />
              )}
              <SectionText html={section} />
            </Section>
          </FourSectionGridItem>
        ))}
        {shouldRenderCtas && (
          <CtasGridItem>
            <Ctas
              noOfSections={sections.length}
              primaryCta={primaryCta}
              secondaryCta={secondaryCta}
            />
          </CtasGridItem>
        )}
      </>
    );
  }

  return (
    <SectionGridItem
      {...sectionsGridItemProps(
        sections.length,
        forceUseAllColumns,
        forceLargeImageOnTablet
      )}
      focusable>
      {shouldDisplayHeadingInline && (
        <InlineHeadingWrapper>
          <Heading>{title}</Heading>
          <Subheading>{subtitle}</Subheading>
        </InlineHeadingWrapper>
      )}
      <SectionsContainer>
        {sections.map((section, index) => (
          <Section
            key={generateKeyForElement([section, index])}
            noOfSections={sections.length}
            index={index}>
            <SectionText html={section} customBulletIcon={customBulletIcon} />
          </Section>
        ))}
      </SectionsContainer>
      {shouldRenderCtas && (
        <Ctas
          noOfSections={sections.length}
          primaryCta={primaryCta}
          secondaryCta={secondaryCta}
        />
      )}
    </SectionGridItem>
  );
};

const MiniGuide: React.FC<MiniGuideProps> = ({
  styledBackground,
  backgroundType,
  title,
  subtitle,
  sections,
  primaryCta,
  secondaryCta,
  image,
  customBulletIcon,
  forceUseAllColumns,
  forceLargeImageOnTablet,
  ...props
}) => {
  const breakpoint = useBreakpoint() ?? 'mobile';
  const shouldDisplayHeadingInline = displayHeadingInline(sections.length, breakpoint);

  return (
    <StyledBackground backgroundType={backgroundType} styledBackground={styledBackground}>
      <MiniGuideContainer {...componentProps(props)}>
        {!shouldDisplayHeadingInline && (
          <Grid>
            <HeadingGridItem {...aboveHeadingGridItemProps}>
              <HeadingWrapper tabIndex={0}>
                <Heading>{title}</Heading>
                <Subheading>{subtitle}</Subheading>
              </HeadingWrapper>
            </HeadingGridItem>
          </Grid>
        )}
        <StyledGrid noOfSections={sections.length}>
          {image && (
            <ImageGridItem
              {...imageGridItemProps(
                sections.length,
                forceUseAllColumns,
                forceLargeImageOnTablet
              )}>
              <StyledImage image={image} />
            </ImageGridItem>
          )}
          <MiniGuideBody
            shouldDisplayHeadingInline={shouldDisplayHeadingInline}
            title={title}
            subtitle={subtitle}
            sections={sections}
            primaryCta={primaryCta}
            secondaryCta={secondaryCta}
            customBulletIcon={customBulletIcon}
            forceUseAllColumns={forceUseAllColumns}
            forceLargeImageOnTablet={forceLargeImageOnTablet}
          />
        </StyledGrid>
      </MiniGuideContainer>
    </StyledBackground>
  );
};

export default applyComponentTheme(MiniGuide, 'MiniGuide');
